/**
 * Retry policy for the study-metadata fetch (case "fails to load" fix).
 *
 * The labeling metadata fetch had no retry, so a transient
 * failure against a cold/lagging backend (5xx), a network blip, or a timeout
 * surfaced as a terminal "Failed to load DICOM metadata" until the reader
 * relaunched — then it "finally loaded" once the backend warmed. This adds a
 * few bounded backoff retries for transient errors while letting terminal
 * states (4xx, incl. the 423 study_not_canonicalized) fail fast with their
 * existing friendly message.
 */

/** Retry only transient failures: no HTTP response (network/timeout) or 5xx.
 *  4xx (incl. 423 study_not_canonicalized) are terminal — do not retry. */
export function isRetryableMetadataError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status == null) return true; // network error / timeout — no response
  return status >= 500;
}

/** Exponential backoff (1-based attempt): 600ms, 1200ms, 2400ms, … capped 5s. */
export function retryDelayMs(attempt: number): number {
  return Math.min(600 * 2 ** (attempt - 1), 5000);
}

interface FetchWithRetryOpts {
  maxAttempts?: number;
  isRetryable?: (err: unknown) => boolean;
  delayMs?: (attempt: number) => number;
  sleep?: (ms: number) => Promise<void>;
  /** Read live; when `aborted` flips true the loop stops retrying. */
  signal?: { aborted: boolean };
}

/**
 * Run `attempt` with bounded backoff retries. Rethrows the last error when it's
 * non-retryable, attempts are exhausted, or the signal aborts. Sleep and the
 * predicate are injectable so the policy is unit-testable without real timers.
 */
export async function fetchWithRetry<T>(
  attempt: (attemptNumber: number) => Promise<T>,
  opts: FetchWithRetryOpts = {},
): Promise<T> {
  const max = opts.maxAttempts ?? 4;
  const isRetryable = opts.isRetryable ?? (() => true);
  const delayMs = opts.delayMs ?? retryDelayMs;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let lastErr: unknown;
  for (let n = 1; n <= max; n++) {
    try {
      return await attempt(n);
    } catch (e) {
      lastErr = e;
      if (n >= max || !isRetryable(e) || opts.signal?.aborted) throw e;
      await sleep(delayMs(n));
      if (opts.signal?.aborted) throw e;
    }
  }
  throw lastErr;
}
